// The dream composer's structured pages — Story · Symbols · Details · Lucidity.
//
// A port of the iOS paged capture in Features/Composer (DreamComposerKit.swift for the controls,
// ComposerView.swift for which field belongs on which page). The preset lists are copied verbatim:
// they are the vocabulary the extraction and archetype code on the server already reasons about, and a
// browser offering "Terror" where the phone offers "Fear" would fragment a person's own history.
//
// Story is the writing surface and stays in ComposerScreen; the three pages here are all optional
// detail, which is why every control has a cleared state and nothing is required to save.
//
// On a wide screen the same fields are shown all at once, stacked into a companion panel beside the
// story instead of hidden behind tabs — see DreamCompanionPanel at the bottom. The field groups are
// shared between the two presentations rather than duplicated: a field that only exists in one of
// them is a field that will be forgotten by whoever changes the other.

import { createContext, useContext, useState, type CSSProperties, type ReactNode } from 'react';
import type { DreamDetails, DreamPerspective, DreamTone } from '../api/entries';
import { IconChevronRight, IconClose } from '../components/Icons';
import { CompanionPanel } from './CompanionPanel';

export type DreamPage = 'story' | 'symbols' | 'details' | 'lucidity';

export const DREAM_PAGES: { id: DreamPage; label: string }[] = [
  { id: 'story', label: 'Story' },
  { id: 'symbols', label: 'Symbols' },
  { id: 'details', label: 'Details' },
  { id: 'lucidity', label: 'Lucidity' },
];

// Verbatim from ComposerView.swift. Do not "improve" the wording on one client only.
const EMOTIONS = ['Love', 'Fear', 'Joy', 'Grief', 'Guilt', 'Shame', 'Envy', 'Anger', 'Awe', 'Peace', 'Longing', 'Confusion'];
const THEMES = ['Love', 'Friendship', 'Family', 'Argument', 'Travel', 'Adventure', 'Fear', 'Loss', 'Success', 'Work', 'Childhood'];
const ACTIONS = ['Running', 'Flying', 'Falling', 'Climbing', 'Hiding', 'Chasing', 'Swimming', 'Searching', 'Fighting', 'Dancing'];
const NATURE = ['Ordinary', 'Precognitive', 'A message', 'Symbolic', 'Healing', 'A visitation', 'Problem-solving'];
const SLEEP_LABELS = ['Poor', 'Restless', 'Fair', 'Good', 'Deep'];
const LUCIDITY_LABELS = ['Asleep', 'Faint', 'Aware', 'Lucid', 'Full control'];
const VIVIDNESS_LABELS = ['Hazy', 'Dim', 'Clear', 'Sharp', 'Vivid'];
const DREAMED_AS = ['Myself', 'Someone else', 'A stranger', 'An animal', 'An observer'];
const SENSES = ['Sound', 'Touch', 'Smell', 'Taste', 'Temperature', 'Motion'];
const REALITY_CHECKS = ['Looked at my hands', 'Tried to fly', 'Read text twice', 'Checked a clock', "Didn't try"];

const TONES: { value: DreamTone; label: string }[] = [
  { value: 'bad', label: 'Bad' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'good', label: 'Good' },
];

const PERSPECTIVES: { value: DreamPerspective; label: string }[] = [
  { value: 'first', label: 'First' },
  { value: 'third', label: 'Third' },
  { value: 'switching', label: 'Switching' },
];

/** Applies one field change without disturbing the rest — including keys a newer iOS may have added. */
type Patch = (patch: Partial<DreamDetails>) => void;

interface PageProps {
  details: DreamDetails;
  onChange: Patch;
}

/**
 * The "let Oreia fill these in" control, above the Symbols fields.
 *
 * A slot rather than the button itself, for the same reason `photos` is one: extraction is a network
 * call against the entry the composer owns, and a form that reached for it would be one more thing to
 * mock. This file stays a form.
 */
interface ExtractSlot {
  extract?: ReactNode;
}

// ─── The page switcher ─────────────────────────────────────────────────

export function DreamPageTabs({
  page,
  onChange,
}: {
  page: DreamPage;
  onChange: (page: DreamPage) => void;
}) {
  return (
    <div className="odtabs" role="tablist" aria-label="Dream pages">
      {DREAM_PAGES.map(({ id, label }) => (
        <button
          key={id}
          role="tab"
          className={`odtab${page === id ? ' on' : ''}`}
          aria-selected={page === id}
          onClick={() => onChange(id)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ─── Shared controls ───────────────────────────────────────────────────

/**
 * Whether a field's label is the document structure or a caption.
 *
 * Inside a tabbed page the label sits under that page's own heading and describes one control, so a
 * span is honest. Stacked into the companion panel there is no page intro and the labels are the only
 * thing a screen reader has to navigate eighteen fields by, so there they have to be real headings.
 */
const LabelsAreHeadings = createContext(false);

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  const asHeading = useContext(LabelsAreHeadings);
  return (
    <div className="odfield">
      <div className="odfield-head">
        {asHeading ? (
          <h3 className="olabel">{label}</h3>
        ) : (
          <span className="olabel">{label}</span>
        )}
        {hint ? <span className="odhint">{hint}</span> : null}
      </div>
      {children}
    </div>
  );
}

function PageIntro({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="odintro">
      <h2>{title}</h2>
      <p>{subtitle}</p>
    </div>
  );
}

/**
 * Commits on Enter, on a typed comma, or via the + button, splitting on commas — the same three ways
 * iOS's `DreamAddInput` accepts a value, so muscle memory carries between the two.
 */
function AddInput({ prompt, onAdd }: { prompt: string; onAdd: (value: string) => void }) {
  const [text, setText] = useState('');

  const commit = (raw: string) => {
    const parts = raw
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    parts.forEach(onAdd);
    setText('');
  };

  return (
    <div className="odadd">
      <input
        className="oinput"
        value={text}
        placeholder={prompt}
        onChange={(e) => {
          const value = e.target.value;
          if (value.includes(',')) commit(value);
          else setText(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(text);
          }
        }}
      />
      {text.trim() ? (
        <button className="odadd-go" onClick={() => commit(text)} aria-label="Add">
          +
        </button>
      ) : null}
    </div>
  );
}

const sameText = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/**
 * Preset chips plus whatever the user typed. Custom values always render active, since the only way
 * one exists is that it was chosen. Matching iOS, adding a value that spells a preset differently
 * snaps to the preset so "fear" and "Fear" never both end up in a list.
 */
function ChipGroup({
  presets,
  selection,
  onChange,
  addPrompt = 'add your own…',
}: {
  presets: string[];
  selection: string[];
  onChange: (next: string[]) => void;
  addPrompt?: string;
}) {
  const toggle = (value: string) => {
    const at = selection.findIndex((s) => sameText(s, value));
    onChange(at >= 0 ? selection.filter((_, i) => i !== at) : [...selection, value]);
  };

  const add = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    const preset = presets.find((p) => sameText(p, value));
    const canonical = preset ?? value;
    if (selection.some((s) => sameText(s, canonical))) return;
    onChange([...selection, canonical]);
  };

  const custom = selection.filter((s) => !presets.some((p) => sameText(p, s)));

  return (
    <div className="odgroup">
      <div className="odchips">
        {presets.map((item) => (
          <button
            key={item}
            className={`ochip${selection.some((s) => sameText(s, item)) ? ' active' : ''}`}
            aria-pressed={selection.some((s) => sameText(s, item))}
            onClick={() => toggle(item)}
          >
            {item}
          </button>
        ))}
        {custom.map((item) => (
          <button key={item} className="ochip active" aria-pressed onClick={() => toggle(item)}>
            {item}
          </button>
        ))}
      </div>
      <AddInput prompt={addPrompt} onAdd={add} />
    </div>
  );
}

/** Free-text tags as removable pills. Commas and Enter both commit, and commas split. */
function TagInput({
  tags,
  onChange,
  placeholder,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
}) {
  const [text, setText] = useState('');

  const commit = (raw: string) => {
    const parts = raw
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length) onChange([...tags, ...parts]);
    setText('');
  };

  return (
    <div className="odgroup">
      {tags.length ? (
        <div className="odtags">
          {tags.map((tag, i) => (
            <span className="odtag" key={`${tag}-${i}`}>
              {tag}
              <button
                onClick={() => onChange(tags.filter((_, j) => j !== i))}
                aria-label={`Remove ${tag}`}
              >
                <IconClose />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <input
        className="oinput"
        value={text}
        placeholder={tags.length ? 'add another…' : placeholder}
        onChange={(e) => {
          const value = e.target.value;
          if (value.includes(',')) commit(value);
          else setText(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(text);
          }
          // Backspace on an empty field takes back the last pill, which is what every tag field
          // people have used before does.
          if (e.key === 'Backspace' && !text && tags.length) onChange(tags.slice(0, -1));
        }}
      />
    </div>
  );
}

/** 1…n dots, filled up to the value, with the level's name beside them. */
function Stepper({
  value,
  labels,
  onChange,
  name,
}: {
  value: number;
  labels: string[];
  onChange: (next: number) => void;
  name: string;
}) {
  return (
    <div className="odstepper">
      <div className="oddots" role="radiogroup" aria-label={name}>
        {labels.map((label, i) => (
          <button
            key={label}
            role="radio"
            aria-checked={value === i + 1}
            aria-label={label}
            className={`oddot${i < value ? ' on' : ''}`}
            onClick={() => onChange(i + 1)}
          />
        ))}
      </div>
      {value >= 1 && value <= labels.length ? (
        <span className="odlevel">{labels[value - 1]}</span>
      ) : null}
    </div>
  );
}

function Segmented<T extends string>({
  items,
  value,
  onChange,
  name,
}: {
  items: { value: T; label: string }[];
  value: T;
  onChange: (next: T) => void;
  name: string;
}) {
  return (
    <div className="odseg" role="radiogroup" aria-label={name}>
      {items.map((item) => (
        <button
          key={item.value}
          role="radio"
          aria-checked={value === item.value}
          className={`odseg-btn${value === item.value ? ' on' : ''}`}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

function ToggleRow({
  label,
  subtitle,
  on,
  onChange,
}: {
  label: string;
  subtitle: string;
  on: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button className="odtoggle" onClick={() => onChange(!on)} aria-pressed={on}>
      <span className="odtoggle-text">
        <span className="odtoggle-label">{label}</span>
        <span className="odtoggle-sub">{subtitle}</span>
      </span>
      <span className={`odswitch${on ? ' on' : ''}`} aria-hidden="true">
        <span />
      </span>
    </button>
  );
}

// ─── Page ② — Symbols & characteristics ────────────────────────────────

export function DreamSymbolsPage({ details, onChange, extract }: PageProps & ExtractSlot) {
  return (
    <div className="odpage">
      <PageIntro
        title="Symbols & characteristics"
        subtitle="the pieces the dream was made of — every field optional"
      />
      <DreamSymbolsFields details={details} onChange={onChange} extract={extract} />
    </div>
  );
}

/** The Symbols fields alone, so the tabbed page and the companion panel show the same controls. */
export function DreamSymbolsFields({ details, onChange, extract }: PageProps & ExtractSlot) {
  return (
    <>
      {extract}
      <Field label="Setting / place">
        <TagInput
          tags={details.setting}
          onChange={(setting) => onChange({ setting })}
          placeholder="beach, old house, school hallway, floating…"
        />
      </Field>
      <Field label="Atmosphere">
        <TagInput
          tags={details.atmosphere}
          onChange={(atmosphere) => onChange({ atmosphere })}
          placeholder="heavy, golden, electric, still…"
        />
      </Field>
      <Field label="Key emotions" hint="tap a few — or add your own">
        <ChipGroup
          presets={EMOTIONS}
          selection={details.emotions}
          onChange={(emotions) => onChange({ emotions })}
        />
      </Field>
      <Field label="Key elements">
        <TagInput
          tags={details.elements}
          onChange={(elements) => onChange({ elements })}
          placeholder="a staircase, a key, rain, a mirror…"
        />
      </Field>
      <Field label="Characters">
        <TagInput
          tags={details.characters}
          onChange={(characters) => onChange({ characters })}
          placeholder="a stranger, my sister, a faceless figure…"
        />
      </Field>
      <Field label="Themes">
        <ChipGroup
          presets={THEMES}
          selection={details.themes}
          onChange={(themes) => onChange({ themes })}
        />
      </Field>
      <Field label="Actions">
        <ChipGroup
          presets={ACTIONS}
          selection={details.actions}
          onChange={(actions) => onChange({ actions })}
        />
      </Field>
    </>
  );
}

// ─── Page ③ — Details ──────────────────────────────────────────────────

export function DreamDetailsPage({ details, onChange }: PageProps) {
  return (
    <div className="odpage">
      <PageIntro title="Details of the dream" subtitle="how it felt, what kind of dream it was" />
      <DreamDetailsFields details={details} onChange={onChange} />
    </div>
  );
}

/** The Details fields alone. See DreamSymbolsFields. */
export function DreamDetailsFields({ details, onChange }: PageProps) {
  return (
    <>
      <Field label="Emotional tone">
        <Segmented
          name="Emotional tone"
          items={TONES}
          value={details.tone}
          onChange={(tone) => onChange({ tone })}
        />
      </Field>
      <Field label="Nature of the dream" hint="a glimpse of the future? a message?">
        <ChipGroup
          presets={NATURE}
          selection={details.dreamNature}
          onChange={(dreamNature) => onChange({ dreamNature })}
        />
      </Field>
      <Field label="Sleep quality">
        <div className="odgroup">
          <Stepper
            name="Sleep quality"
            value={details.sleepQuality}
            labels={SLEEP_LABELS}
            onChange={(sleepQuality) => onChange({ sleepQuality })}
          />
          {details.sleepQuality > 0 ? (
            <button className="olink-plain odclear" onClick={() => onChange({ sleepQuality: 0 })}>
              Clear
            </button>
          ) : null}
        </div>
      </Field>
      <ToggleRow
        label="Recurring dream"
        subtitle="I've had this dream before"
        on={details.recurring}
        onChange={(recurring) => onChange({ recurring })}
      />
    </>
  );
}

// ─── Page ④ — Lucidity & texture ───────────────────────────────────────

export function DreamLucidityPage({ details, onChange }: PageProps) {
  return (
    <div className="odpage">
      <PageIntro title="Lucidity" subtitle="awareness, texture, and point of view" />
      <DreamLucidityFields details={details} onChange={onChange} />
    </div>
  );
}

/** The Lucidity fields alone. See DreamSymbolsFields. */
export function DreamLucidityFields({ details, onChange }: PageProps) {
  const toggleSense = (sense: string) => {
    const has = details.sensoryAnchors.includes(sense);
    onChange({
      sensoryAnchors: has
        ? details.sensoryAnchors.filter((s) => s !== sense)
        : [...details.sensoryAnchors, sense],
    });
  };

  const isCustomCheck =
    details.realityCheck !== '' && !REALITY_CHECKS.includes(details.realityCheck);

  return (
    <>
      <Field label="Lucidity level">
        <Stepper
          name="Lucidity level"
          value={details.lucidity}
          labels={LUCIDITY_LABELS}
          onChange={(lucidity) => onChange({ lucidity })}
        />
      </Field>
      <Field label="Vividness">
        <Stepper
          name="Vividness"
          value={details.vividness}
          labels={VIVIDNESS_LABELS}
          onChange={(vividness) => onChange({ vividness })}
        />
      </Field>
      <Field label="Perspective">
        <Segmented
          name="Perspective"
          items={PERSPECTIVES}
          value={details.perspective}
          onChange={(perspective) => onChange({ perspective })}
        />
      </Field>
      <Field label="Dreamed as" hint="who were you in the dream?">
        <ChipGroup
          presets={DREAMED_AS}
          selection={details.dreamedAs}
          onChange={(dreamedAs) => onChange({ dreamedAs })}
        />
      </Field>
      <Field label="Sensory anchors" hint="beyond sight">
        {/* Fixed list, no add-your-own — iOS builds this one from chips alone. */}
        <div className="odchips">
          {SENSES.map((sense) => (
            <button
              key={sense}
              className={`ochip${details.sensoryAnchors.includes(sense) ? ' active' : ''}`}
              aria-pressed={details.sensoryAnchors.includes(sense)}
              onClick={() => toggleSense(sense)}
            >
              {sense}
            </button>
          ))}
        </div>
      </Field>
      <Field label="Reality check attempted?">
        <div className="odgroup">
          <div className="odchips">
            {REALITY_CHECKS.map((check) => (
              <button
                key={check}
                className={`ochip${details.realityCheck === check ? ' active' : ''}`}
                aria-pressed={details.realityCheck === check}
                onClick={() =>
                  onChange({ realityCheck: details.realityCheck === check ? '' : check })
                }
              >
                {check}
              </button>
            ))}
            {isCustomCheck ? (
              <button className="ochip active" aria-pressed onClick={() => onChange({ realityCheck: '' })}>
                {details.realityCheck}
              </button>
            ) : null}
          </div>
          {/* One check, not a list, so this replaces rather than appends. */}
          <AddInput
            prompt="…or write your own check"
            onAdd={(value) => onChange({ realityCheck: value })}
          />
        </div>
      </Field>
    </>
  );
}

// ─── The desktop companion panel ───────────────────────────────────────

/**
 * Open by default, and remembered. Same key shape and same 'open' / 'closed' values as the chat
 * screen's conversations panel (oreia.chat.library), because they are the same kind of decision and
 * a reader of localStorage should not have to learn two conventions.
 */
const COMPANION_KEY = 'oreia.composer.companion';

/**
 * All four dream pages at once, as a facing page beside the story.
 *
 * Only rendered on a wide screen — ComposerScreen decides, because the tabs and this column are
 * different content rather than one markup laid out twice, and rendering both would put every
 * control on the page twice. It scrolls on its own: the point of the panel is that a long dream
 * cannot scroll the fields away while you are writing.
 *
 * The chrome — heading, collapse tab, remembered state — is CompanionPanel, shared with the panel a
 * waking entry gets.
 */
export function DreamCompanionPanel({
  details,
  onChange,
  onImagine,
  premium = false,
  photos,
  extract,
  tinted = false,
  suppressed = false,
  style,
}: PageProps & ExtractSlot & {
  /** Opens the Dream Vision flow. Left unset for now — see the note at the call site. */
  onImagine?: () => void;
  premium?: boolean;
  /**
   * The entry's pictures, at the top of the panel. A slot rather than the photo components
   * themselves: which photos an entry has, and what stands in when it has none, is the composer's
   * business, and a dream form that reached for a decrypted photo would be one more thing to mock.
   */
  photos?: ReactNode;
  /** The card's reading surface, when one has been chosen. See the call site. */
  tinted?: boolean;
  /** Stood down while a reading is open in front of it. See CompanionPanel. */
  suppressed?: boolean;
  style?: CSSProperties;
}) {
  return (
    <CompanionPanel
      id="composer-companion"
      title="The dream"
      storageKey={COMPANION_KEY}
      showLabel="Show dream details"
      hideLabel="Hide dream details"
      surface="weave"
      tinted={tinted}
      suppressed={suppressed}
      style={style}
    >
      {photos}

      {/* The one deliberate no-op in here: the Dream Vision flow this opens is being built
          separately, and `onImagine` is the whole of the wiring when it lands. */}
      <button className="ocomp-imagine" onClick={onImagine}>
        <span>
          <span className="t">Imagine this dream</span>
          <span className="s">{premium ? 'Premium · monthly allowance' : 'One to try · up to 10 a month with Premium'}</span>
        </span>
        <IconChevronRight />
      </button>

      <LabelsAreHeadings value>
        <DreamSymbolsFields details={details} onChange={onChange} extract={extract} />
        <DreamDetailsFields details={details} onChange={onChange} />
        <DreamLucidityFields details={details} onChange={onChange} />
      </LabelsAreHeadings>
    </CompanionPanel>
  );
}
